import { FC } from 'react';

import { HotColumn } from '@handsontable/react';
import { IList } from 'core/API/listApi';
import { LocaleKey, useTranslation } from 'helpers/languages/translations';
import { MPdpComplianceTargetHeader, translateHeaders } from './helpers/HeadersList';
import { requireTitle } from './helpers/utils';
import Grid from '../Grid';
import { listValidator, numberValidator } from '../Validators';
import { AutoCompleteRenderer, CustomIntervalRenderer } from './helpers/customRenderers';
import { IRetailer } from 'shared/types';
import { keyBy } from 'lodash';

export const MPdpComplianceTarget: FC<{
    list: IList;
    toolId: number;
    retailers: IRetailer[];
    allRetailers: boolean;
    useRetailerIds?: boolean;
    numeric?: boolean;
}> = ({ list, toolId, retailers, allRetailers, useRetailerIds, numeric }) => {
    const translatedHeaders = translateHeaders(MPdpComplianceTargetHeader);
    const translate = useTranslation();
    const allRetailersString = translate('list.allRetailers') as string;
    const retailerNames = [
        retailers[0].name,
        ...retailers
            .slice(1)
            .map((ret) => ret.name)
            .sort()
    ];

    const retailersById = keyBy(retailers, 'id');
    const retailerColumnId = list.columns.findIndex((column) => column.includes('retailer'));

    const newData = useRetailerIds
        ? list.data.map((row) => {
            const formattedRow = [...row];
            // Will uncomment later
            // if (formattedRow[retailerColumnId] === 0) formattedRow[retailerColumnId] = allRetailersString;
            formattedRow[retailerColumnId] = retailersById[row[retailerColumnId] as number]?.name || '';
            return formattedRow;
        })
        : list.data;

    const formattedList = { ...list, data: newData };

    const afterValidate = (prevList: IList) => {
        if (!useRetailerIds) return prevList;
        const retailersByName = keyBy(retailers, 'name');
        const newData = prevList.data.map((row) => {
            if (row[0] === translate('list.allRetailers')) row[0] = 0;
            else row[0] = retailersByName[row[0] as string]?.id;

            row[1] = !row[1] ? null : Number(row[1]);

            return row;
        });
        return {
            ...prevList,
            data: newData,
        };
    };

    return (
        <Grid toolId={toolId} headers={MPdpComplianceTargetHeader} list={formattedList} afterValidate={afterValidate}>
            <HotColumn
                title={requireTitle(translatedHeaders[0])}
                validator={listValidator(allRetailers ? [allRetailersString, ...retailerNames] : retailerNames)}
                source={allRetailers ? [allRetailersString, ...retailerNames] : retailerNames}
                type="autocomplete"
            >
                {allRetailers ? (
                    <AutoCompleteRenderer list={[allRetailersString, ...retailerNames]} hot-renderer />
                ) : (
                    <AutoCompleteRenderer list={retailerNames} hot-renderer />
                )}
            </HotColumn>
            <HotColumn
                type={numeric ? 'numeric' : 'text'}
                validator={numberValidator(false, { min: 0, max: 100 })}
                allowEmpty={false}
                title={requireTitle(translatedHeaders[1])}
            >
                <CustomIntervalRenderer min={1} max={100} hot-renderer />
            </HotColumn>
        </Grid>
    );
};
